import { useCallback, useRef, useState } from 'react';
import ContractTemplateService, {
  CONTRACT_TEMPLATE_DOCX_CONTENT_TYPE,
  CONTRACT_TEMPLATE_MAX_FILE_SIZE_BYTES,
} from '@clickersphere/core/services/ContractTemplateService';

interface IUploadContractTemplateFileResult {
  fileKey: string;
}

export const TEMPLATE_UPLOAD_INVALID_FILE_ERROR = 'invalid_file';
export const TEMPLATE_UPLOAD_FILE_TOO_LARGE_ERROR = 'file_too_large';

export function useUploadContractTemplateFile() {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const uploadContractTemplateFileAsync = useCallback(
    async (file: File): Promise<IUploadContractTemplateFileResult> => {
      setProgress(0);
      setIsUploading(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const isDocx =
          file.type === CONTRACT_TEMPLATE_DOCX_CONTENT_TYPE ||
          file.name.toLowerCase().endsWith('.docx');

        if (!isDocx) {
          throw new Error(TEMPLATE_UPLOAD_INVALID_FILE_ERROR);
        }

        if (file.size > CONTRACT_TEMPLATE_MAX_FILE_SIZE_BYTES) {
          throw new Error(TEMPLATE_UPLOAD_FILE_TOO_LARGE_ERROR);
        }

        const target = await ContractTemplateService.getUploadUrl({
          contentType: CONTRACT_TEMPLATE_DOCX_CONTENT_TYPE,
          fileSize: file.size,
        });

        await ContractTemplateService.uploadFileWithProgress(
          target,
          file,
          (percent) => setProgress(percent),
          controller.signal,
        );

        return { fileKey: target.fileKey };
      } finally {
        setIsUploading(false);
        abortControllerRef.current = null;
      }
    },
    [],
  );

  const abortUpload = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    uploadContractTemplateFileAsync,
    abortUpload,
    progress,
    isUploading,
  };
}
